import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { TRIP_STATUS_ORDER, TRIP_TRANSITIONS, getPhaseProgress } from './tripPhases';
import { libelle, tableAvecRepli } from './libelles';
import { arrondiDevise, formatDate as formatDateFr, formatDateHeure } from './formats';

export {
  TRIP_STATUS_ORDER,
  TRIP_PHASE_IMPORT,
  TRIP_PHASE_DELIVERY,
  TRIP_PHASE_CLOSED,
  PHASE_LABELS,
  getTripPhase,
  phaseLabel,
  phaseStatuses,
  getPhaseProgress,
  TRIP_TRANSITIONS,
} from './tripPhases';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function normalizeTripStatus(status) {
  return status === 'RETURNING' ? 'COMPLETED' : status;
}

// Libellés de statut de trajet: alias de `LIBELLES.tripStatus` (lib/libelles.js).
// Les clés énumérées sont celles du parcours; tout autre code se lit quand même
// en français (« Livraison partielle », jamais PARTIALLY_DELIVERED).
export const TRIP_STATUS_LABELS = tableAvecRepli('tripStatus', [
  ...TRIP_STATUS_ORDER,
  'RETURNING',
  'CANCELLED',
]);

export const TRIP_STATUS_FLOW = [...TRIP_STATUS_ORDER, 'CANCELLED'];

// Progression du trajet, exprimée dans sa phase courante (importation ou
// livraison) — voir lib/tripPhases.js.
export function getTripProgress(status) {
  return getPhaseProgress(status);
}

// Get status badge class
export function getStatusBadgeClass(status) {
  const normalizedStatus = status === 'RETURNING' ? 'COMPLETED' : status;
  if (['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(status)) {
    return 'status-customs pulse-customs';
  }
  if (
    [
      'EN_ROUTE_TO_BORDER',
      'EN_ROUTE_TO_LOADING',
      'EN_ROUTE_TO_ENTRY_CUSTOMS',
      'ARRIVED_AT_DEPOT',
      'EN_ROUTE_TO_DELIVERY',
    ].includes(status)
  ) {
    return 'status-en-route';
  }
  if (status === 'LOADING') {
    return 'status-loading';
  }
  if (status === 'ARRIVED_AT_DEPOT') {
    return 'status-delivered';
  }
  if (status === 'DELIVERED') {
    return 'status-delivered';
  }
  if (normalizedStatus === 'COMPLETED') {
    return 'status-completed';
  }
  if (status === 'CANCELLED') {
    return 'status-cancelled';
  }
  return 'status-created';
}

// Alias de `LIBELLES.truckStatus`, `technicalStatus` et `operationType`.
export const TRUCK_STATUS_LABELS = tableAvecRepli('truckStatus', [
  'FREE',
  'ON_TRIP',
  'MAINTENANCE',
  'RENTED_OUT',
  'IMMOBILIZED',
  'RETURNED_TO_OWNER',
]);

export const TECHNICAL_STATUS_LABELS = tableAvecRepli('technicalStatus');

export const OPERATION_TYPE_LABELS = tableAvecRepli('operationType');

// Dates: toujours en français (voir lib/formats.js). Vide ou illisible → « - ».
export function formatDate(dateString) {
  return formatDateFr(dateString, '-');
}

export function formatDateTime(dateString) {
  return formatDateHeure(dateString, '-');
}

// Format relative time
export function formatRelativeTime(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return formatDate(dateString);
}

// Format number with thousands separator
// Vide, NaN, Infinity ou texte illisible → « - », jamais « NaN » à l'écran.
// Un nombre reçu en texte (« 1200.5 », Decimal sérialisé) est lu comme un nombre.
const nombreFini = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '' || typeof valeur === 'boolean') return null;
  const nombre = Number(valeur);
  return Number.isFinite(nombre) ? nombre : null;
};

export function formatNumber(num) {
  const nombre = nombreFini(num);
  if (nombre === null) return '-';
  return nombre.toLocaleString('fr-FR');
}

// Format currency (CFA)
export function formatCurrency(amount) {
  const nombre = nombreFini(amount);
  if (nombre === null) return '-';
  // Même arrondi que le serveur: `Math.round(-2.5)` donnait −2 au lieu de −3.
  return `${formatNumber(arrondiDevise(nombre, 'XOF'))} FCFA`;
}

/**
 * Montants et volumes abrégés, pour les tuiles d'indicateurs.
 *
 * « 163 144 000 FCFA » fait seize caractères. Cinq tuiles sur une ligne de
 * 1440 px en laissent la place à huit, et le chiffre partait en points de
 * suspension: « 163 144 000 F… ». Un montant tronqué ne s'arrondit pas dans la
 * tête du lecteur, il devient illisible.
 *
 * L'abrégé n'est pas une perte d'information à cet endroit: une tuile répond à
 * « de quel ordre de grandeur parle-t-on », le montant exact vit sur la ligne
 * de détail. C'est l'usage courant au Mali, où l'on compte en millions de
 * francs CFA et où « 163 M FCFA » se dit à voix haute.
 */
export function formatCompactNumber(value) {
  if (value === null || value === undefined || value === '') return '-';
  const nombre = Number(value);
  if (!Number.isFinite(nombre)) return '-';

  const signe = nombre < 0 ? '-' : '';
  const absolu = Math.abs(nombre);

  // En dessous du million, le nombre entier tient et reste plus parlant:
  // « 847 000 » se lit mieux que « 0,8 M ».
  if (absolu < 1_000_000) return `${signe}${formatNumber(Math.round(absolu))}`;

  const [diviseur, suffixe] =
    absolu >= 1_000_000_000 ? [1_000_000_000, ' Md'] : [1_000_000, ' M'];
  const reduit = absolu / diviseur;
  // Une décimale sous 100, aucune au-delà: « 163 M » et non « 163,1 M », qui
  // donne une fausse impression de précision sur un ordre de grandeur.
  const decimales = reduit < 100 ? 1 : 0;
  return `${signe}${reduit.toLocaleString('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })}${suffixe}`;
}

export function formatCompactCurrency(amount) {
  if (nombreFini(amount) === null) return '-';
  return `${formatCompactNumber(amount)} FCFA`;
}

export function formatCompactVolume(liters) {
  if (nombreFini(liters) === null) return '-';
  return `${formatCompactNumber(liters)} L`;
}

// Format volume (liters)
export function formatVolume(liters) {
  if (nombreFini(liters) === null) return '-';
  return `${formatNumber(liters)} L`;
}

export const DEFAULT_STOCK_THRESHOLDS = {
  low: 20000,
  high: 2200000,
};

export function getStockAlert(
  stockLiters,
  thresholds = DEFAULT_STOCK_THRESHOLDS,
) {
  const stock = Number(stockLiters || 0);
  const low = Number(thresholds?.low ?? DEFAULT_STOCK_THRESHOLDS.low);
  const high = Number(thresholds?.high ?? DEFAULT_STOCK_THRESHOLDS.high);

  if (stock <= low) {
    return {
      level: 'low',
      label: 'Stock bas',
      message: `Sous le seuil bas (${formatNumber(low)} L)`,
    };
  }

  if (stock >= high) {
    return {
      level: 'high',
      label: 'Surstock',
      message: `Au-dessus du seuil haut (${formatNumber(high)} L)`,
    };
  }

  return {
    level: 'normal',
    label: 'Stock normal',
    message: 'Niveau de stock stable',
  };
}

/**
 * La jauge de stock, la même à l'accueil et sur la page Stock.
 *
 * Aucune capacité de cuve n'est déclarée: le cumul importé sert de plafond,
 * et à défaut le seuil haut. L'accueil calculait le pourcentage sur les
 * imports (33 %) mais affichait en légende « Capacité totale 2 200 000 L »
 * (le seuil haut): 45 000 L ne font pas 33 % de 2 200 000 L.
 */
export function jaugeStock(litresEnStock, litresImportes) {
  const courant = Number(litresEnStock) || 0;
  const importes = Number(litresImportes) || 0;
  const capacite = importes > 0 ? importes : DEFAULT_STOCK_THRESHOLDS.high;
  const niveau = Math.max(0, Math.min(100, Math.round((courant / capacite) * 100)));
  return { niveau, capacite };
}

// Get next possible statuses for a trip
// Les étapes réellement atteignables depuis le statut courant.
// L'écran proposait auparavant tous les statuts suivants: le serveur n'accepte
// que les transitions adjacentes, donc chaque saut affiché finissait en erreur.
// Sans dépôt (`managesDepot === false`), l'étape « Arrivé au dépôt » n'est
// pas proposée: le camion part directement en livraison.
export function getNextStatuses(currentStatus, { managesDepot = true } = {}) {
  const normalizedStatus = currentStatus === 'RETURNING' ? 'COMPLETED' : currentStatus;
  const forward = (TRIP_TRANSITIONS[normalizedStatus] || []).filter(
    (status) => managesDepot || status !== 'ARRIVED_AT_DEPOT',
  );
  if (!forward.length) return [];
  return ['CANCELLED', ...forward];
}

// Check if status is a customs status
export function isCustomsStatus(status) {
  return ['AT_CUSTOMS_EXIT', 'AT_CUSTOMS_ENTRY'].includes(status);
}

// Generate a simple ID
export function generateId() {
  return crypto.randomUUID();
}

/** Noms des formules de licence: alias de `LIBELLES.licensePlan`. */
export const PACK_LABELS = tableAvecRepli('licensePlan', [
  'TRANSPORT_DISTRIBUTION',
  'CONTROLE_CARBURANT',
  'STATIONS_RESEAU',
]);

export function packLabel(key) {
  if (!key) return '—';
  return libelle('licensePlan', key);
}
